import { Card, Grid } from '@design-systems/canvas-ui'
import { useEffect, useState, useRef } from 'react'
import { Table } from 'components/table-compat/table-compat'
import { format, parseISO } from 'date-fns'
import { useStoreTrailerDetails } from 'hooks/trailer/useStoreTrailerDetails'
import { useEssentials } from 'hooks/utils/useEssentials'
import {
    addLinkToFirstColumn,
    buildDataTableObject,
    Cell,
    TableData,
} from 'utils/data'
import { ErrorBoundary } from 'react-error-boundary'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import { MPMLayout } from 'components/layout'
import { UrlParams } from 'enums/url-params'
import { BasicPlaceholder } from 'components/placeholder'
import { Constants } from './messages'
import { useNavigate } from 'react-router-dom'
import { TrailerEta } from './store-trailer-eta'
import { createLeadTimeCell } from '../helpers'
import {
    extractLaborHours,
    createCustomBlockRows,
    getDataRows,
    sortTableRows,
    getFieldIndex,
    createLoadZoneRows,
    createChildRowsForLoadZone,
} from './helpers'

import './store-trailer-details.scss'
import { TrailerWorkArea } from './store-trailer-work-area'
import { BackButton } from 'components/button/back-button'
import { useLogClick } from 'utils/analytics'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
export const StoreTrailerDetailsPage = () => {
    const { t } = useEssentials()
    const { trailer } = TRANSLATION_KEYS

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(trailer.title)}
                />
            )}
        >
            <MPMLayout
                heading={t(trailer.title)}
                breadcrumbsEnabled={false}
                isMenuOverride={true}
                menuOverride={<BackButton />}
            >
                <StoreTrailerDetails />
            </MPMLayout>
        </ErrorBoundary>
    )
}

export const StoreTrailerDetails = () => {
    const navigate = useNavigate()
    const { trailer: trailerKeys } = TRANSLATION_KEYS
    const { details } = trailerKeys
    const { table } = details

    const [customTableData, setCustomTableData] = useState<TableData>()
    const [loadZoneTableData, setLoadZoneTableData] = useState<TableData>()
    const [currentLoadZone, setCurrentLoadZone] = useState<string>('')
    const [selectedDataView, setSelectedDataView] = useState<number>(0)
    const bulkDataInitializedRef = useRef(false)
    const { t, env, session, currentLocation, params } = useEssentials()
    const { logClick } = useLogClick('trailerDetails')
    const hasLoggedRef = useRef(false)
    const moveId = params.get(UrlParams.MOVE_ID)
    useEffect(() => {
        if (!moveId) {
            void navigate(`/trailer?locationId=${currentLocation}`, {
                replace: true,
            })
        }
    }, [navigate, moveId, currentLocation])

    const {
        data: currentTrailer,
        isLoading,
        isError,
        error,
        refetch,
    } = useStoreTrailerDetails({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        moveId: moveId ?? '',
    })

    useEffect(() => {
        if (!hasLoggedRef.current) {
            logClick('trailer details')
            hasLoggedRef.current = true
        }
    }, [logClick])

    const bulk_items = currentTrailer?.bulk_items ?? []
    const custom_block_detail_counts_arr =
        currentTrailer?.custom_block_detail_counts ?? []
    const hasBulkItems = bulk_items.length > 0
    const hasCustomBlocks =
        (customTableData?.rows?.length ||
            custom_block_detail_counts_arr.length) > 0

    useEffect(() => {
        // Initialize to show bulk data if no custom block data exists
        if (
            !bulkDataInitializedRef.current &&
            !hasCustomBlocks &&
            hasBulkItems
        ) {
            bulkDataInitializedRef.current = true
            setSelectedDataView(1)
        }
    }, [hasBulkItems, hasCustomBlocks])

    // Show loading state until trailer data is fully populated
    if (!currentTrailer?.trailer_id || isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    const { labor_types, load_zone_detail_counts } = currentTrailer

    const formattedClosedTime = currentTrailer?.shipment_closed_timestamp
        ? format(
              parseISO(currentTrailer.shipment_closed_timestamp),
              'M/d h:mm a',
          )
        : 'N/A'

    const leadTimeCell = createLeadTimeCell(
        String(currentTrailer.lead_time ?? ''),
    )

    const stockingTime = extractLaborHours(labor_types, Constants.STOCKING)
    const secondarySortingTime = extractLaborHours(
        labor_types,
        Constants.SECONDARY_SORTING,
    )

    const { customBlockRowsWithZero, customBlockRowsWithoutZero } = getDataRows(
        createCustomBlockRows(custom_block_detail_counts_arr),
    )

    let customBlockTable = {
        headings: [
            table.customBlock,
            table.caseCartons,
            table.repackCartons,
            table.stockingTime,
            table.secondarySortingTime,
        ].map(
            (value: string): Cell => ({
                value: t(value),
            }),
        ),
        rows: [...customBlockRowsWithoutZero, ...customBlockRowsWithZero],
    }
    const initRowSort = customBlockTable.rows.sort((a, b) => {
        if (a[0].value.includes('00') || b[0].value.includes('00')) {
            return 0
        }
        const aValue = Number(a[1].value)
        const bValue = Number(b[1].value)
        return bValue - aValue
    })
    const {
        customBlockRowsWithoutZero: sortedRowsWithoutZero,
        customBlockRowsWithZero: sortedRowsWithZero,
    } = getDataRows(initRowSort)
    customBlockTable.rows = [...sortedRowsWithoutZero, ...sortedRowsWithZero]
    const sortData = (sortDirection: 'asc' | 'desc', field: string) => {
        const fieldIndex = getFieldIndex(field)
        const newData = {
            ...customBlockTable,
            rows: customBlockTable?.rows ?? [],
        }

        const { customBlockRowsWithZero, customBlockRowsWithoutZero } =
            getDataRows(newData.rows)
        const sortedRowsWithoutZero = sortTableRows(
            customBlockRowsWithoutZero,
            fieldIndex,
            sortDirection,
        )

        newData.rows = [...sortedRowsWithoutZero, ...customBlockRowsWithZero]
        customBlockTable = newData
        setCustomTableData(customBlockTable)
    }

    const bulkItemsTable = {
        headings: [
            table.dpci,
            table.description,
            table.cartons,
            table.eaches,
        ].map(
            (value: string): Cell => ({
                value: t(value),
            }),
        ),
        rows: bulk_items.map((item) => [
            { value: item.dpci },
            { value: item.description },
            { value: String(item.cartons) },
            { value: String(item.eaches) },
        ]),
    }

    const load_zone_detail_counts_arr = load_zone_detail_counts ?? []

    const loadZoneDataRows = createLoadZoneRows(load_zone_detail_counts_arr)
    const linkedLoadZoneRows = addLinkToFirstColumn(loadZoneDataRows)
    const initialLoadZoneSortRows = linkedLoadZoneRows.sort((a, b) => {
        // Sort by load zone name (first column) alphabetically
        const aValue = a[0].value
        const bValue = b[0].value
        return aValue.localeCompare(bValue)
    })

    let loadZoneTable = {
        headings: [
            table.loadZone,
            table.caseCartons,
            table.repackCartons,
            table.stockingTime,
            table.secondarySortingTime,
        ].map(
            (value: string): Cell => ({
                value: t(value),
            }),
        ),
        rows: [...initialLoadZoneSortRows],
    }

    const sortLoadZoneData = (sortDirection: 'asc' | 'desc', field: string) => {
        const fieldIndex = getFieldIndex(field)
        const currentTableData = loadZoneTableData || loadZoneTable

        if (!currentTableData.rows) return

        const hasMixedData = currentTableData.rows.some((row) => row[0].childOf)

        if (!hasMixedData) {
            // Simple case: only parent load zone rows
            const sortedLoadZoneRows = sortTableRows(
                linkedLoadZoneRows,
                fieldIndex,
                sortDirection,
            )
            loadZoneTable.rows = addLinkToFirstColumn(sortedLoadZoneRows)
            setLoadZoneTableData(loadZoneTable)
        } else {
            // Complex case: mixed parent/child rows - preserve parent order, sort children within parents
            const newRows: Cell[][] = []
            const processedParents = new Set<string>()

            currentTableData.rows.forEach((row) => {
                const isChildRow = row[0].childOf

                if (!isChildRow) {
                    const parentName = row[0].value
                    if (!processedParents.has(parentName)) {
                        processedParents.add(parentName)
                        newRows.push(row)

                        const childRows = currentTableData.rows.filter(
                            (r) => r[0].childOf === parentName,
                        )
                        const sortedChildren = sortTableRows(
                            childRows,
                            fieldIndex,
                            sortDirection,
                        )
                        newRows.push(...sortedChildren)
                    }
                }
            })

            const updatedTable = { ...currentTableData, rows: newRows }
            setLoadZoneTableData(updatedTable)
        }
    }

    const buildLoadZoneTableRows = (expandedLoadZone: string): Cell[][] => {
        const flattenedRows: Cell[][] = []

        // Get the sorted order from initialLoadZoneSortRows to maintain consistent ordering
        const sortedLoadZoneNames = initialLoadZoneSortRows.map(
            (row) => row[0].value,
        )

        sortedLoadZoneNames.forEach((loadZoneName) => {
            const item = load_zone_detail_counts_arr.find(
                (item) => item.name === loadZoneName,
            )
            if (!item) return

            const isExpanded = item.name === expandedLoadZone
            const baseRow = createLoadZoneRows([item])
            const parentRows = addLinkToFirstColumn(baseRow)
            flattenedRows.push(...parentRows)

            if (isExpanded) {
                const childRows = createChildRowsForLoadZone(
                    item,
                    expandedLoadZone,
                )
                flattenedRows.push(...childRows)
            }
        })

        return flattenedRows
    }

    const handleLoadZoneClick = (clickedLoadZone: string) => {
        const matchedItem = load_zone_detail_counts_arr.find(
            (item) => item.name === clickedLoadZone,
        )
        const nextLoadZone =
            matchedItem?.name === currentLoadZone
                ? ''
                : (matchedItem?.name ?? '')

        setCurrentLoadZone(nextLoadZone)

        const flattenedRows = buildLoadZoneTableRows(nextLoadZone)
        loadZoneTable.rows = flattenedRows
        setLoadZoneTableData(loadZoneTable)
    }
    return (
        <Grid.Container className="hc8-pa-1x" spacing="expanded">
            <Grid.Item
                className="hc8-pb-none"
                size={{
                    xs: 12,
                }}
            >
                <Card variant="elevated">
                    <TrailerEta moveId={moveId} />
                </Card>
            </Grid.Item>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <Card variant="elevated">
                    <p className="hc8-ma-none hc8-pa-3x">
                        {`${t(details.billOfLading)}:`} {moveId}
                    </p>
                    {currentTrailer.type === 'FDC' && (
                        <p className="hc8-ma-none hc8-pa-3x">
                            {currentTrailer.acknowledged_timestamp
                                ? `${t(trailerKeys.summary.acknowledgedTitle)}:`
                                : `${t(trailerKeys.summary.unacknowledgedTransitTitle)}:`}{' '}
                            {currentTrailer.miles_from_store != null
                                ? `${currentTrailer.miles_from_store} ${t(trailerKeys.details.miles)}`
                                : t(trailerKeys.details.unknownMiles)}
                        </p>
                    )}
                    <div className="hc8-pt-none">
                        <Table
                            data={buildDataTableObject({
                                currentData: {
                                    headings: [
                                        table.type,
                                        table.closed,
                                        table.trailerNumber,
                                        table.cartons,
                                        table.leadTime,
                                    ].map(
                                        (value: string): Cell => ({
                                            value: t(value),
                                        }),
                                    ),
                                    rows: [
                                        [
                                            { value: '' + currentTrailer.type },
                                            { value: '' + formattedClosedTime },
                                            {
                                                value:
                                                    '' +
                                                    currentTrailer.trailer_id,
                                            },
                                            {
                                                value:
                                                    '' + currentTrailer.cartons,
                                            },
                                            leadTimeCell,
                                        ],
                                    ],
                                },
                                widths: [60, 60, 60, 60, 60],
                                isLoading,
                            })}
                            name={'store-trailer-details'}
                            scrollableWidth={false}
                        />
                    </div>
                    <div className="hc8-pt-4x hc8-pb-3x">
                        <Table
                            data={buildDataTableObject({
                                currentData: {
                                    headings: [
                                        table.laborHours,
                                        table.stockingTime,
                                        table.secondarySortingTime,
                                    ].map(
                                        (value: string): Cell => ({
                                            value: t(value),
                                        }),
                                    ),
                                    rows: [
                                        [
                                            t(table.total),
                                            stockingTime,
                                            secondarySortingTime,
                                        ].map(
                                            (value: any): Cell => ({
                                                value: '' + value,
                                            }),
                                        ),
                                    ],
                                },
                                widths: [90, 90, 90],
                                isLoading,
                            })}
                            name={'store-trailer-details'}
                            scrollableWidth={false}
                        />
                    </div>
                </Card>

                <div className="hc8-pt-4x">
                    {currentTrailer && (
                        <TrailerWorkArea data={currentTrailer} />
                    )}
                </div>

                {(hasCustomBlocks || hasBulkItems) && (
                    <>
                        <Grid.Container
                            align="center"
                            justify="center"
                            className="hc8-mb-3x hc8-mt-2x"
                        >
                            <Grid.Item>
                                <GeneralizedComponent.Buttons
                                    items={[
                                        t(table.customBlock),
                                        t(table.bulk),
                                    ]}
                                    selected={selectedDataView}
                                    setSelected={setSelectedDataView}
                                    kpiName="store trailer details data view"
                                    logClick={logClick}
                                />
                            </Grid.Item>
                        </Grid.Container>
                        <div className="store-trailer-details-custom-block">
                            {selectedDataView === 0 && (
                                <Table
                                    data={buildDataTableObject({
                                        currentData:
                                            customTableData! ||
                                            customBlockTable,
                                        widths: [120, 90, 90, 90, 100],
                                        isLoading,
                                        onSort: sortData,
                                        sortable: true,
                                        pinFirstColumn: true,
                                        columnSortSettingsByIndex: {
                                            1: {
                                                sortable: true,
                                                sortType: 'numeric',
                                                defaultSort: 'dsc',
                                            },
                                        },
                                    })}
                                    name="store-trailer-details-custom-block"
                                    alternateRowColor={true}
                                    scrollableWidth={true}
                                />
                            )}
                            {selectedDataView === 1 && (
                                <Table
                                    data={buildDataTableObject({
                                        currentData: bulkItemsTable,
                                        widths: [120, 200, 90, 90],
                                        isLoading,
                                        sortable: false,
                                        columnSortSettingsByIndex: {
                                            2: {
                                                sortable: true,
                                                sortType: 'numeric',
                                            },
                                            3: {
                                                sortable: true,
                                                sortType: 'numeric',
                                            },
                                        },
                                    })}
                                    name="store-trailer-details-bulk-items"
                                    alternateRowColor={true}
                                    scrollableWidth={true}
                                />
                            )}
                        </div>
                    </>
                )}

                {(loadZoneTableData?.rows?.length ||
                    loadZoneTable?.rows?.length ||
                    load_zone_detail_counts_arr.length) > 0 && (
                    <div className="hc8-pt-4x">
                        <Table
                            data={buildDataTableObject({
                                currentData:
                                    loadZoneTableData! || loadZoneTable,
                                widths: [120, 70, 70, 80, 100],
                                isLoading,
                                onSort: sortLoadZoneData,
                                sortable: true,
                                pinFirstColumn: true,
                                handleClick: handleLoadZoneClick,
                                columnSortSettingsByIndex: {
                                    0: {
                                        sortable: true,
                                        sortType: 'character',
                                        defaultSort: 'asc',
                                    },
                                },
                            })}
                            name="store-trailer-details-load-zone"
                            scrollableWidth={true}
                            alternateRowColor={true}
                            data-testid="load-zone-table"
                        />
                    </div>
                )}
            </Grid.Item>
        </Grid.Container>
    )
}
